import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { postData } from "../../utility/Utility";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import GreetingCardModal from "../../Common/GreetingCardModal";

function GreetingCardCalendar() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showGreetingCardModal, setShowGreetingCardModal] = useState(false);
  const [greetingCards, setGreetingCards] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch greeting cards from API
  const fetchGreetingCards = async () => {
    try {
      const response = await postData("api/greeting-cards/list", {});
      if (response?.success && Array.isArray(response.data?.cards)) {
        setGreetingCards(response.data.cards);
      } else {
        setGreetingCards([]);
      }
    } catch (error) {
      console.error("Error fetching greeting cards:", error);
      setGreetingCards([]);
    }
  };

  // Fetch holidays from API (same as Holiday Calendar)
  const fetchHolidays = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await postData("api/event-templates/holiday", {});

      if (response?.success && response.data) {
        setHolidays(Array.isArray(response.data) ? response.data : []);
      } else {
        setHolidays([]);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      setError("Error fetching holidays");
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGreetingCards();
    fetchHolidays();
  }, []);

  // Check if date is current or future
  const isCurrentOrFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate >= today;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Format date for display
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get all greeting cards for a date
  const getGreetingCardsForDate = (date) => {
    if (!greetingCards || greetingCards.length === 0) return [];

    const dateString = formatDateToString(date);
    return greetingCards.filter(card => card.scheduledDate === dateString);
  };

  // Get all holidays for a date (same as Holiday Calendar)
  const getAllFestivalsForDate = (date) => {
    if (!holidays || holidays.length === 0) return [];

    const dateString = date.toISOString().split('T')[0]; // Format to YYYY-MM-DD to match API response
    return holidays.filter(holiday => holiday.date === dateString);
  };

  // Custom tile content to show holidays and greeting cards
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const festivals = getAllFestivalsForDate(date);
      const cards = getGreetingCardsForDate(date);
      const isFutureDate = isCurrentOrFutureDate(date);

      if (festivals.length === 0 && cards.length === 0) return null;

      return (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
          {/* Show festivals first (holidays) */}
          {festivals.length > 0 && (
            <div
              className={`text-center fw-bold w-100 px-1 ${festivals[0].type.toLowerCase().includes('gazetted') || festivals[0].type.toLowerCase().includes('restricted') ? 'text-info' : 'text-success'} ${isFutureDate ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
              title={festivals.map(f => f.name).join(', ')}
              style={{
                fontSize: '0.7rem',
                lineHeight: '1.1',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isFutureDate) {
                  handleFestivalClick(date);
                }
              }}
            >
              {festivals[0].name}
              {festivals.length > 1 && (
                <span
                  className="badge bg-secondary ms-1"
                  style={{
                    fontSize: '0.5rem',
                    padding: '1px 3px'
                  }}
                >
                  +{festivals.length - 1}
                </span>
              )}
            </div>
          )}

          {/* Show greeting cards below festivals */}
          {cards.length > 0 && (
            <div
              className={`text-center fw-bold w-100 px-1 text-primary ${isFutureDate ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
              title={cards.map(c => c.title).join(', ')}
              style={{
                fontSize: '0.7rem',
                lineHeight: '1.1',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block',
                marginTop: festivals.length > 0 ? '2px' : '0'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isFutureDate) {
                  handleGreetingCardClick(date);
                }
              }}
            >
              {cards[0].title}
              {cards.length > 1 && (
                <span
                  className="badge bg-secondary ms-1"
                  style={{
                    fontSize: '0.5rem',
                    padding: '1px 3px'
                  }}
                >
                  +{cards.length - 1}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Handle festival click (same as Holiday Calendar)
  const handleFestivalClick = (date) => {
    if (!isCurrentOrFutureDate(date)) {
      return;
    }

    setSelectedDate(date);
    setShowGreetingCardModal(true);
  };

  // Handle greeting card click
  const handleGreetingCardClick = (date) => {
    if (!isCurrentOrFutureDate(date)) {
      return;
    }

    setSelectedDate(date);
    setShowGreetingCardModal(true);
  };

  // Handle date click on calendar - ONLY FOR FUTURE DATES
  const handleDateClick = (date) => {
    // Only allow clicks on current or future dates
    if (!isCurrentOrFutureDate(date)) {
      return;
    }

    setSelectedDate(date);
    setShowGreetingCardModal(true);
  };

  // Custom class for tiles - DISABLE PAST DATES (same as Holiday Calendar)
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const festival = getAllFestivalsForDate(date).length > 0;
      const cards = getGreetingCardsForDate(date).length > 0;
      const today = isToday(date);
      const isFutureDate = isCurrentOrFutureDate(date);

      let className = 'border-0 ';

      if (festival) {
        className += 'bg-light ';
      }
      if (cards) {
        className += 'bg-light '; // You can add a different class if needed
      }
      if (today) {
        className += 'bg-warning text-dark ';
      }
      if (!isFutureDate) {
        className += 'past-date disabled-date ';
      }

      return className.trim();
    }
    return '';
  };

  // Custom navigation label
  const navigationLabel = ({ date }) => {
    return formatDateForDisplay(date);
  };

  // Add custom CSS for disabled dates
  const calendarStyles = `
    .react-calendar__tile.past-date {
      background-color: #f8f9fa !important;
      color: #6c757d !important;
      cursor: not-allowed !important;
    }

    .react-calendar__tile.past-date:hover {
      background-color: #f8f9fa !important;
    }

    .react-calendar__tile.disabled-date {
      pointer-events: none !important;
    }

    .react-calendar__tile:not(.past-date) {
      cursor: pointer !important;
    }

    /* Better text overflow handling */
    .react-calendar__tile > div > div {
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
  `;

  if (error) {
    return (
      <div className="content-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-12">
              <div className="card shadow">
                <div className="card-body text-center py-5">
                  <div className="text-danger mb-3">
                    <i className="mdi mdi-alert-circle-outline" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="text-danger">Failed to load data</h5>
                  <p className="text-muted">{error}</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => {
                      fetchGreetingCards();
                      fetchHolidays();
                    }}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <style>{calendarStyles}</style>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-12">
            <div className="card shadow">
              <div className="card-header bg-primary-1 text-white text-center">
                <h4 className="card-title mb-1">
                  <i className="mdi mdi-calendar-text me-2"></i>
                  Greeting Card & Holiday Calendar
                </h4>
                <p className="card-text text-white-50 mb-0">
                  {greetingCards.length > 0 || holidays.length > 0
                    ? `Showing ${greetingCards.length} greeting cards and ${holidays.length} holidays. Click on any current or future date to create greeting cards.`
                    : 'Click on any current or future date to create greeting cards.'
                  }
                </p>
              </div>

              <div className="card-body">
                <Calendar
                  onClickDay={handleDateClick}
                  tileContent={tileContent}
                  tileClassName={tileClassName}
                  navigationLabel={navigationLabel}
                  className="w-100"
                  showNeighboringMonth={false}
                  minDetail="month"
                  maxDetail="month"
                />
              </div>

              {/* Enhanced Legend */}
              <div className="card-footer">
                <div className="row text-center">
                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                    <span className="badge bg-success me-2">Festival</span>
                    <small>Festival/Observance</small>
                  </div>
                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                    <span className="badge bg-info me-2">Holiday</span>
                    <small>Gazetted/Restricted</small>
                  </div>
                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                    <span className="badge bg-primary me-2">Greeting Card</span>
                    <small>Has Greeting Card</small>
                  </div>
                  <div className="col-12 col-md-3">
                    <span className="badge bg-warning text-dark me-2">Today</span>
                    <small>Today</small>
                  </div>
                </div>
                <div className="row text-center mt-2">
                  <div className="col-12">
                    <small className="text-muted">
                      <strong>Tip:</strong> You can click on any current or future date (with or without holidays/greeting cards) to create greeting cards
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Greeting Card Modal */}
        <GreetingCardModal
          isOpen={showGreetingCardModal}
          onClose={() => {
            setShowGreetingCardModal(false);
            setSelectedDate(null);
          }}
          selectedDate={selectedDate}
          festival={selectedDate ? getAllFestivalsForDate(selectedDate)[0] : null}
          onGreetingCardCreated={() => {
            setShowGreetingCardModal(false);
            setSelectedDate(null);
          }}
        />
      </div>
    </div>
  );
}

export default GreetingCardCalendar;
